/** @format */
import {useTheme} from '@mui/material';

import {
  CartesianGrid,
  ScatterChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Scatter,
  Tooltip,
  ReferenceArea,
} from 'recharts';

import {
  ChartWrapper,
  ChartTitle,
  getAxisLabelStyle,
  getAxisTickStyle,
  ChartSubTitle,
} from './styles';
import {GoalFactAiChartProps, DataPoint} from './types';
import CustomDot from './CustomDot';
import CustomLine from './CustomLine';
import CustomTooltip from './CustomTooltip';
import {formatAxisValue} from './helpers';

const GoalFactAiChart = ({
  targetBudget,
  targetKpi,
  actualBudget,
  actualKpi,
  expectedBudget,
  expectedKpi,
  kpiName,
  kpiUnits,
}: GoalFactAiChartProps) => {
  const theme = useTheme();

  const data: DataPoint[] = [
    {
      name: 'Цель',
      budget: targetBudget || actualBudget,
      kpi: targetKpi,
      color: theme.palette.semantic_dark?.primary?.main ?? '',
      radius: 20,
    },
    {
      name: 'Факт',
      budget: actualBudget,
      kpi: actualKpi,
      color: theme.palette.semantic_dark?.alert?.main ?? '',
      radius: 20,
    },
    {
      name: 'ИИ',
      budget: expectedBudget,
      kpi: expectedKpi,
      color: theme.palette.semantic_dark?.good?.main ?? '',
      radius: 20,
    },
  ];

  const budgets = data.map(d => d.budget);
  const kpis = data.map(d => d.kpi);

  const minBudget = Math.min(...budgets);
  const maxBudget = Math.max(...budgets);
  const minKpi = Math.min(...kpis);
  const maxKpi = Math.max(...kpis);

  // Увеличен padding для лучшей читаемости и чтобы точки не ютились по углам
  const budgetPadding = (maxBudget - minBudget) * 0.4;
  const kpiPadding = (maxKpi - minKpi) * 0.4;

  const budgetDomain = [Math.max(0, minBudget - budgetPadding), maxBudget + budgetPadding];
  const kpiDomain = [Math.max(0, minKpi - kpiPadding), maxKpi + kpiPadding];
  const targetBudgetValue = targetBudget || actualBudget;
  const budgetMin5 = Math.max(budgetDomain[0], targetBudgetValue * 0.95);
  const budgetMax5 = Math.min(budgetDomain[1], targetBudgetValue * 1.05);

  const targetPosition = ((targetBudgetValue - budgetMin5) / (budgetMax5 - budgetMin5)) * 100;

  const targetKpiValue = targetKpi;

  const budgetTicks = Array.from({length: 6}, (_, i) => {
    const step = (budgetDomain[1] - budgetDomain[0]) / 5;
    return budgetDomain[0] + step * i;
  });

  const kpiTicks = Array.from({length: 6}, (_, i) => {
    const step = (kpiDomain[1] - kpiDomain[0]) / 5;
    return kpiDomain[0] + step * i;
  });

  const axisLabelStyle = getAxisLabelStyle(theme);
  const axisTickStyle = getAxisTickStyle(theme);

  return (
    <ChartWrapper>
      <ChartTitle>Текущий статус и потенциал проекта</ChartTitle>
      <ChartSubTitle>
        Потенциал улучшения KPI и бюджета если применить все рекомендации
      </ChartSubTitle>
      <ResponsiveContainer width='100%' height='80%'>
        <ScatterChart
          margin={{
            top: 20,
            right: 30,
            bottom: 20,
            left: 20,
          }}
        >
          <defs>
            <linearGradient id='horizontalFill' x1='0' y1='0' x2='1' y2='0'>
              <stop offset='0%' stopColor='#2D2E32' stopOpacity={0.2} />
              <stop offset={`${targetPosition}%`} stopColor='#419466' stopOpacity={0.4} />
              <stop offset='100%' stopColor='#2D2E32' stopOpacity={0.2} />
            </linearGradient>
            <linearGradient id='verticalFill' x1='0' y1='0' x2='0' y2='1'>
              <stop
                offset='0%'
                stopColor={theme.palette.semantic_dark?.good?.main}
                stopOpacity={0.2}
              />
              <stop
                offset='100%'
                stopColor={theme.palette.semantic_dark?.good?.main}
                stopOpacity={0.2}
              />
            </linearGradient>
          </defs>
          <CartesianGrid
            stroke={theme.palette.semantic_dark?.surface?.surface2}
            strokeDasharray='0'
          />
          {/* Вертикальная заливка от значения цели вниз по оси Y (ниже значения цели = к нулю) */}
          <ReferenceArea
            x1={budgetDomain[0]}
            x2={budgetDomain[1]}
            y1={kpiDomain[0]}
            y2={targetKpiValue}
            fill='url(#verticalFill)'
            fillOpacity={1}
          />
          {/* Горизонтальная заливка от значения цели вправо по оси X - выше по z-index */}
          <ReferenceArea
            x1={budgetMin5}
            x2={budgetMax5}
            y1={kpiDomain[0]}
            y2={kpiDomain[1]}
            fill='url(#horizontalFill)'
            fillOpacity={1}
          />
          <XAxis
            type='number'
            dataKey='budget'
            name='Бюджет'
            domain={budgetDomain}
            ticks={budgetTicks}
            tickFormatter={formatAxisValue}
            axisLine={false}
            tickLine={false}
            label={{
              value: 'Бюджет, ₽',
              position: 'insideBottomLeft',
              offset: 0,
              style: axisLabelStyle,
            }}
            tick={axisTickStyle}
          />
          <YAxis
            type='number'
            dataKey='kpi'
            name={kpiName}
            domain={kpiDomain}
            ticks={kpiTicks}
            tickFormatter={formatAxisValue}
            axisLine={false}
            tickLine={false}
            label={{
              value: `${kpiName} (меньше — лучше), ${kpiUnits}`,
              angle: -90,
              position: 'insideBottom',
              offset: 80,
              style: axisLabelStyle,
            }}
            tick={axisTickStyle}
          />
          <Tooltip content={<CustomTooltip />} cursor={{strokeDasharray: '3 3'}} />
          <Scatter
            name='Показатели'
            data={data}
            shape={<CustomDot />}
            line={<CustomLine />}
            isAnimationActive
          />
        </ScatterChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
};

export default GoalFactAiChart;
